<form class="patient-form" [formGroup]="patientForm" autocomplete="off">
  <h1>{{ pageTitle }}</h1>
  <p>* = required</p>
  <div class="fieldset-container">
    <p-fieldset legend="Identification" [toggleable]="true">
      <div class="form-container">
        <emr-input type="text" label="First Name *" [control]="firstNameFormControl"></emr-input>
        <emr-input type="text" label="Middle Name" [control]="middleNameFormControl"></emr-input>
        <emr-input type="text" label="Last Name *" [control]="lastNameFormControl"></emr-input>
      </div>
      <div class="form-container">
        <emr-input type="text" label="Medical Record Number *" [control]="idFormControl"
          [bottomSpace]="true"></emr-input>
        <button *ngIf="isCreateType" pButton pRipple class="mrn-btn" type="button" label="Generate MRN"
          class="p-button-outlined" (click)="generateMRN()"></button>
      </div>
      <!-- Errors for MRN -->
      <span *ngIf="
          isCreateType && idFormControl.invalid && (idFormControl.dirty || idFormControl.touched)
        ">
        <span *ngIf="idFormControl.errors?.['required']" class="error-msg"> MRN is required. </span>
        <span *ngIf="idFormControl.errors?.['uniqueEntry']" class="error-msg">
          This MRN is already in use by another patient.
        </span>
      </span>
    </p-fieldset>
  </div>

  <div class="fieldset-container" formGroupName="generalInfo">
    <p-fieldset legend="General Information" [toggleable]="true">
      <div class="form-container">
        <emr-input type="date" label="Date of Birth *" [control]="dobFormControl" [bottomSpace]="true"></emr-input>
        <emr-input type="number" label="Age *" hint="Number from 0-130" [control]="ageFormControl"
          [min]="minMeasurement" [max]="maxAge"></emr-input>
        <emr-input type="select" label="Sex *" [control]="sexFormControl" [options]="sexOptions"
          [bottomSpace]="true"></emr-input>
      </div>
      <div class="form-container">
        <emr-input type="number" label="Weight (kilograms) *" hint="Number from 0-300" [control]="weightFormControl"
          [min]="minMeasurement" [max]="maxWeight"></emr-input>
        <emr-input type="number" label="Height (feet) *" hint="Number from 0-10" [control]="heightFeetFormControl"
          [min]="minMeasurement" [max]="maxFeet"></emr-input>
        <emr-input type="number" label="Height (inches) *" hint="Number from 0-11" [control]="heightInchesFormControl"
          [min]="minMeasurement" [max]="maxInches"></emr-input>
      </div>
      <div class="form-container">
        <emr-input type="select" label="Code Status *" [control]="codeStatusFormControl"
          [options]="codeStatusOptions"></emr-input>
        <emr-input type="text" label="Isolation *" [control]="isolationFormControl" />
      </div>
      <div class="form-container">
        <emr-input type="select" label="Type *" [control]="patientTypeFormControl" [options]="patientTypeOptions" />
        <emr-input type="text" label="Room #" [control]="roomNumberFormControl" />
        <emr-input type="text" label="Location (e.g., PCP office, School Nurse Office, ICU, etc.)"
          [control]="locationFormControl" />
      </div>
    </p-fieldset>
  </div>

  <div *ngIf="isCreateType" class="btn-container">
    <div>
      <button pButton pRipple type="button" label="Randomize" (click)="randomizeForm()"></button>
    </div>
    <p>
      <b>Note:</b> This button is a simple randomizer. Be sure to check the randomized values and
      change any that may not line up with others.
    </p>
  </div>


  <div class="fieldset-container">
    <p-fieldset legend="Medical Information" [toggleable]="true">
      <p class="med-info-note">
        <b>Note:</b> If the patient does not have any interdisciplinary team individuals, active
        medical problems, or allergies, make sure to delete the form boxes under that specific
        category in order to submit the form.
      </p>

      <div class="med-info">
        <h2>Interdisciplinary Team</h2>
        <ng-container formArrayName="interdisciplinaryTeam">
          <ng-container *ngFor="let providerFormGroup of interdisciplinaryTeamFormArray.controls; let i = index"
            [formGroupName]="i">
            <div class="med-info-form-row">
              <div class="med-info-input-div">
                <label for="providerSpecialization">Provider {{ i + 1 }} Specialization</label>
                <input id="providerSpecialization" type="text" pInputText formControlName="specialization" />
              </div>
              <div class="med-info-input-div">
                <label for="providerName">Provider {{ i + 1 }} Name</label>
                <input id="providerName" type="text" pInputText formControlName="name" />
              </div>
              <div class="med-info-input-div">
                <label for="providerTitle">Provider {{ i + 1 }} Title</label>
                <input id="providerTitle" type="text" pInputText formControlName="title" />
              </div>
              <button pButton pRipple type="button" icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-text" (click)="deleteFormGroup(i, 'team')"></button>
            </div>
          </ng-container>
          <button pButton pRipple type="button" icon="pi pi-plus" class="p-button-rounded"
            (click)="addEmptyFormGroup('team')"></button>
        </ng-container>
      </div>

      <p-divider></p-divider>

      <div class="med-info">
        <h2>Active Medical Problems</h2>
        <ng-container formArrayName="activeMedicalProblems">
          <ng-container *ngFor="let medProblemFormGroup of activeMedProblemsFormArray.controls; let i = index"
            [formGroupName]="i">
            <div class="med-info-form-row">
              <div class="med-info-input-div">
                <label for="medicalProblem">Medical Problem {{ i + 1 }} Name</label>
                <input id="medicalProblem" type="text" pInputText formControlName="name" />
              </div>
              <div class="med-info-input-div">
                <label for="altName">Enter alternate name (if any)</label>
                <input id="altName" type="text" pInputText formControlName="altName" />
              </div>
              <button pButton pRipple type="button" icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-text" (click)="deleteFormGroup(i, 'problem')"></button>
            </div>
          </ng-container>
          <button pButton pRipple type="button" icon="pi pi-plus" class="p-button-rounded"
            (click)="addEmptyFormGroup('problem')"></button>
        </ng-container>
      </div>

      <p-divider></p-divider>

      <div class="med-info">
        <h2>Allergies</h2>
        <ng-container formArrayName="allergies">
          <ng-container *ngFor="let allergyFormGroup of allergiesFormArray.controls; let i = index" [formGroupName]="i">
            <div class="med-info-form-row">
              <div class="med-info-input-div">
                <label for="medicalProblem">Allergy {{ i + 1 }}</label>
                <input id="medicalProblem" type="text" pInputText formControlName="name" />
              </div>
              <div class="med-info-input-div">
                <label for="altName">Enter alternate name (if any)</label>
                <input id="altName" type="text" pInputText formControlName="altName" />
              </div>
              <button pButton pRipple type="button" icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-text" (click)="deleteFormGroup(i, 'allergy')"></button>
            </div>
          </ng-container>
          <button pButton pRipple type="button" icon="pi pi-plus" class="p-button-rounded"
            (click)="addEmptyFormGroup('allergy')"></button>
        </ng-container>
      </div>
    </p-fieldset>
  </div>
  <div class="fieldset-container">
    <p-fieldset legend="Upload Profile Picture" [toggleable]="true">
      <p-fileUpload accept="image/*" [maxFileSize]="500000" chooseLabel="Browse Files" [showUploadButton]="false"
        [showCancelButton]="false" (onSelect)="onFileChange($event)" (onRemove)="onFileChange($event)"></p-fileUpload>
    </p-fieldset>
  </div>
  <div class="submit-btn-container">
    <button pButton [label]="submitButtonLabel" type="button" (click)="upsertPatient()" [disabled]="!patientForm.valid"
      [loading]="didSubmit"></button>
  </div>
</form>